import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import {
  cmdbChangesQuery,
  cmdbProblemsQuery,
  cmdbRecordQuery,
  cmdbSecurityQuery,
  cmdbSyslogQuery,
  type CiChangesPanel,
  type CiEdge,
  type CiRecordPage,
  type CiSyslogPanel,
} from '../api/cmdb'
import { ApiError } from '../api/client'
import { Spinner } from '../ui/Spinner'
import { LoadFailed } from '../ui/LoadFailed'
import { isForbidden } from '../ui/Forbidden'
import { EdgeRelation } from './EdgeRelation'
import { HistoryIntegrity } from './HistoryIntegrity'
import { ScanPortsTable } from './ScanCards'
import { ElsewhereTable, MachineLink, programRows } from './SoftwareCards'
import './CmdbRowTabs.css'

/**
 * Randul desfacut din lista CMDB - `Cmdb::getActionTabs()` si fiecare
 * `Cmdb::row*()`, plus coloana de Quick Access.
 *
 * ---------------------------------------------------------------------------
 * ACEEASI BANDA, ACEEASI ORDINE, ACELEASI CUVINTE
 * ---------------------------------------------------------------------------
 *
 * Portalul trimitea o bucata de HTML pe care `ExpandRow` din `general.js` o
 * punea in rand: opt file intr-un `ul.action_tabs`, si langa ele „Quick Access"
 * cu Advanced si Map. Aici e desenata in React, dar cu ACELEASI opt file, in
 * aceeasi ordine, cu aceleasi nume - inclusiv „Events", care in portal e fila de
 * syslog. Ordinea nu e intamplatoare si sta scrisa in `action_tabs.php`:
 * Summary spune ce ESTE lucrul; Impact ce cade daca el cade; Security pe unde
 * poate fi impins sa cada; Problems de cate ori a cazut deja.
 *
 * ---------------------------------------------------------------------------
 * DE UNDE VINE FIECARE FILA
 * ---------------------------------------------------------------------------
 *
 * Summary, Asset, Relations, Changes si Impact citesc `GET /cmdb/ci/{id}` -
 * ACELASI raspuns pe care il citeste fisa. O a doua cale ar fi insemnat ca
 * acelasi element arata altceva desfacut in lista decat deschis pe fisa, si
 * cele doua s-ar fi despartit la prima coloana adaugata intr-una din ele.
 *
 * Events si Problems isi au capetele lor, fiindca poarta filtre si numaratori
 * pe care fisa nu le cere.
 *
 * Security isi are si ea capatul ei, de pe 16 septembrie 2026. Pana atunci
 * spunea pe fata ca nu raspunde de aici, fiindca potrivirea de vulnerabilitati
 * statea intr-o clasa pe care v4 n-o putea incarca. Intre timp v4 si-a facut
 * portul ei (`App\Softsee\Cve\DbalCveMatcher`), folosit deja de fisa masinii,
 * deci fila nu scrie a doua potrivire - o cheama pe aceea. Vezi `CiTabs`.
 *
 * ---------------------------------------------------------------------------
 * GARDUL NU E AICI
 * ---------------------------------------------------------------------------
 *
 * Fiecare capat isi cere dreptul (`inventoree.cmdb.read`) si isi verifica
 * elementul fata de firmele omului, pe SERVER. O fila nedesenata n-a oprit
 * niciodata pe nimeni sa ceara adresa de mana.
 */

type Tab = 'summary' | 'asset' | 'relations' | 'changes' | 'impact' | 'security' | 'problems' | 'events'

/** Numele si adresa fiecarei file, EXACT ca in `action_tabs.php`. */
const TABS: { key: Tab; label: string; path: string }[] = [
  { key: 'summary', label: 'Summary', path: 'rowSummary' },
  { key: 'asset', label: 'Asset', path: 'rowAsset' },
  { key: 'relations', label: 'Relations', path: 'rowRelations' },
  { key: 'changes', label: 'Changes', path: 'rowChanges' },
  { key: 'impact', label: 'Impact', path: 'rowImpact' },
  { key: 'security', label: 'Security', path: 'rowSecurity' },
  { key: 'problems', label: 'Problems', path: 'rowProblems' },
  { key: 'events', label: 'Events', path: 'rowSyslog' },
]

const DASH = '—'

/** `row_security.php` - aceleasi culori si aceleasi cuvinte ca in portal. */
const SEVERITY_ORDER = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'NONE']

const SEVERITY_COLOURS: Record<string, string> = {
  CRITICAL: '#a0362a',
  HIGH: '#c2571f',
  MEDIUM: '#96562a',
  LOW: '#5a6472',
  NONE: '#7c8896',
}

const SEVERITY_LABELS: Record<string, string> = {
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
  NONE: 'Not scored',
}

/** Culoarea propozitiei care numara ce atinge productia. */
const SEVERITY_PRODUCTION = '#a0362a'

/* Foaia randului de prospetime, ca in portal: trei reguli nu cer o foaie
   comuna, iar `ui/` nu se atinge pentru atat. */
const SECURITY_CSS = `
.cve-freshness { display: flex; align-items: baseline; gap: 8px; margin: 14px 0 0; padding: 8px 10px; border-radius: 4px; font-size: 12px; line-height: 1.5; }
.cve-freshness i { flex: none; }
.cve-freshness-hint { display: block; opacity: .85; margin-top: 2px; }
.cve-freshness-ok   { background: #f1f7f3; color: #3d6b52; }
.cve-freshness-warn { background: #fdf3e3; color: #8a6410; }
.cve-freshness-bad  { background: #fdeceb; color: #a0362a; }
`

function text(value: string | number | null | undefined): string {
  return value === null || value === undefined || value === '' ? DASH : String(value)
}

export function CmdbRowTabs({ id }: { id: number }) {
  const [tab, setTab] = useState<Tab>('summary')

  return (
    <div className="row d-flex m-0" data-ci={id}>
      <div className="col-auto bg-lightest-gray">
        <ul className="action_tabs">
          {TABS.map((entry) => (
            <li key={entry.key}>
              <a
                href={`/app/cmdb/${entry.path}/${id}`}
                className={tab === entry.key ? 'action_button active' : 'action_button'}
                onClick={(e) => {
                  e.preventDefault()
                  setTab(entry.key)
                }}
              >
                {entry.label}
              </a>
            </li>
          ))}
        </ul>

        {/*
          Quick Access iese din rand; filele raman inauntru. Aceleasi doua
          butoane ca in portal, in aceleasi culori de modul: Inventoree pentru
          fisa, Softsee pentru harta. „Advanced", nu „Record": fila de aici
          arata deja fisa in mic, iar cuvantul trebuie sa spuna ca dincolo e mai
          mult, nu ca e acelasi lucru.
        */}
        <div className="quick-view-modules">
          <p style={{ fontSize: 14, padding: '4px 6px', color: '#000' }}>
            <i className="fas fa-bolt" aria-hidden="true"></i> Quick Access
          </p>
          <a
            title="Everything about this item: properties, connections, impact, tickets, and what the relay observed"
            href={`/app/cmdb/view/${id}`}
            className="adm-quick adm-quick-inventoree"
            target="_blank"
            rel="noopener"
          >
            <i className="fas fa-id-card"></i> Advanced
          </a>
          <a
            title="Where it sits in the map"
            href={`/app/adm/map?root=${id}`}
            className="adm-quick adm-quick-softsee"
            target="_blank"
            rel="noopener"
          >
            <i className="fas fa-project-diagram"></i> Map
          </a>
        </div>
      </div>

      <div className="col-md-12">
        <div className="details_content cmdb-row-panels">
          {tab === 'events' ? (
            <EventsTab id={id} />
          ) : tab === 'problems' ? (
            <ProblemsTab id={id} />
          ) : tab === 'security' ? (
            <SecurityTab id={id} />
          ) : (
            <RecordTab id={id} tab={tab} />
          )}
        </div>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- *
 * Filele care citesc fisa
 * -------------------------------------------------------------------------- */

function RecordTab({ id, tab }: { id: number; tab: Tab }) {
  const page = useQuery(cmdbRecordQuery(id))

  if (page.error) return <PanelStopped error={page.error} retry={() => void page.refetch()} what="This item" />
  if (!page.data) return <Spinner />

  const data = page.data
  if (tab === 'summary') return <SummaryPanel page={data} />
  if (tab === 'asset') return <AssetPanel page={data} />
  if (tab === 'relations') return <RelationsPanel page={data} />
  if (tab === 'changes') return <ChangesPanel id={id} />
  return <ImpactPanel page={data} />
}

function SummaryPanel({ page }: { page: CiRecordPage }) {
  const ci = page.ci
  return (
    <div className="row" style={{ padding: '12px 16px' }}>
      <div className="col-md-6">
        <table className="table table-condensed">
          <tbody>
            <Row label="Class" value={text(ci.className)} />
            <Row
              label="Status"
              value={<span className={`adm-pill ${ci.statusPill}`}>{text(ci.statusLabel)}</span>}
            />
            <Row
              label="Health"
              value={<span className={`adm-pill ${ci.healthPill}`}>{text(ci.healthLabel)}</span>}
            />
            <Row
              label="Importance"
              value={<span className={ci.importance.class}>{text(ci.importance.label)}</span>}
            />
            <Row label="Environment" value={text(ci.environmentLabel)} />
            <Row label="Customer" value={text(ci.companyName)} />
            <Row label="First seen" value={text(ci.firstSeen)} muted />
            <Row label="Last seen" value={text(ci.lastSeen)} muted />
            <Row
              label="Maintained by"
              value={
                ci.manual ? (
                  'Added manually'
                ) : ci.refType === 2 ? (
                  /* `refId` e aparatul DESCOPERIT, nu o fisa de inventar: un link
                     catre /inventoree/device/{refId} ar deschide alta masina. */
                  <>
                    <i className="fas fa-satellite-dish text-muted"></i> Network scan, no agent
                  </>
                ) : ci.refType === 3 ? (
                  /* `refId` e randul de INSTALARE al programului, nu o masina:
                     un link catre /inventoree/device/{refId} ar deschide alta
                     masina. Masina vine din cardul programului. */
                  <>
                    <i className="fas fa-cube text-muted"></i> Software inventory
                    {page.software?.machine && (
                      <>
                        {' — '}
                        <MachineLink machine={page.software.machine} />
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <i className="fas fa-robot text-muted"></i> Discovery
                    {ci.refId > 0 && (
                      <>
                        {' — '}
                        <a href={`/app/inventoree/device/${ci.refId}`} target="_blank" rel="noopener">
                          {ci.deviceName === '' ? `device #${ci.refId}` : ci.deviceName}
                        </a>
                      </>
                    )}
                  </>
                )
              }
            />
          </tbody>
        </table>
      </div>
      <div className="col-md-6">
        {page.fields.length === 0 ? (
          <p className="text-muted">
            This class has no attributes defined. Attributes come from{' '}
            <em>Settings → Inventoree → Type form builder</em>.
          </p>
        ) : (
          <table className="table table-condensed">
            <tbody>
              {page.fields.map((field) => (
                <tr key={field.id}>
                  <td style={{ width: 150 }} className="text-muted">
                    {field.label}
                  </td>
                  <td>{text(field.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function AssetPanel({ page }: { page: CiRecordPage }) {
  const ci = page.ci
  if (page.scan) return <ScanAssetPanel page={page} />
  if (page.software) return <SoftwareAssetPanel page={page} />
  return (
    <div style={{ padding: '12px 16px' }}>
      <div className="row">
        <div className="col-md-6">
          <table className="table table-condensed">
            <tbody>
              <Row label="Serial" value={text(ci.serial)} />
              <Row label="Operating system" value={text(ci.osVersion)} />
              <Row label="Last user" value={text(ci.lastUser)} />
              <Row label="Bought" value={text(ci.acquisitionDate)} />
              <Row label="Warranty ends" value={text(ci.warrantyExpiry)} />
              <Row label="Extended warranty" value={text(ci.extendedWarrantyExpiry)} />
              <Row label="Last confirmed" value={text(ci.verifiedAt)} muted />
            </tbody>
          </table>
        </div>
        <div className="col-md-6">
          <p className="text-muted small" style={{ marginBottom: 6 }}>
            The newest {page.ticketLimit} tickets raised on this item — {page.openTickets} still open.
          </p>
          {page.tickets.length === 0 ? (
            <p className="text-muted">No ticket has ever named this item.</p>
          ) : (
            <table className="table table-condensed table-striped">
              <tbody>
                {page.tickets.map((ticket) => (
                  <tr key={ticket.id}>
                    <td style={{ width: 70 }}>
                      <a href={`/app/easeedesk/ticket/${ticket.id}`} target="_blank" rel="noopener">
                        {ticket.id}
                      </a>
                    </td>
                    <td>{ticket.subject}</td>
                    <td style={{ width: 120 }}>
                      <span className={`adm-pill ${ticket.state.pill}`}>{ticket.state.label}</span>
                    </td>
                    <td style={{ width: 150 }} className="text-muted">
                      {ticket.opened}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Fila Asset a unui PROGRAM (`refType` 3): n-are serial, garantie sau ultim
 * utilizator - alea sunt ale masinii. Se arata ce stie inventarul agentului
 * despre instalare, si pe ce alte masini ale clientului mai e.
 */
function SoftwareAssetPanel({ page }: { page: CiRecordPage }) {
  const software = page.software
  if (!software) return null
  return (
    <div style={{ padding: '12px 16px' }} id="cmdbSoftwareAsset">
      <p className="text-muted small" style={{ marginBottom: 8 }}>
        <i className="fas fa-cube"></i> A program, not a machine - data from the agent&apos;s software inventory.
      </p>
      <div className="row">
        <div className="col-md-6">
          <table className="table table-condensed">
            <tbody>
              {programRows(software).map(([label, value]) => (
                <Row key={label} label={label} value={value === null || value === '' ? DASH : value} />
              ))}
            </tbody>
          </table>
        </div>
        <div className="col-md-6">
          <p className="text-muted small" style={{ marginBottom: 4 }}>
            Also installed on {software.elsewhere.total} other machine{software.elsewhere.total === 1 ? '' : 's'} of this
            customer
          </p>
          <ElsewhereTable software={software} />
        </div>
      </div>
    </div>
  )
}

/**
 * Fila Asset a unui aparat DESCOPERIT: n-are fisa de inventar (serial,
 * garantie, ultimul utilizator - le scrie agentul), deci se arata ce a gasit
 * scanarea, si se spune de ce restul lipseste.
 */
function ScanAssetPanel({ page }: { page: CiRecordPage }) {
  const scan = page.scan
  if (!scan) return null
  const id = scan.identity
  return (
    <div style={{ padding: '12px 16px' }} id="cmdbScanAsset">
      <p className="text-muted small" style={{ marginBottom: 8 }}>
        <i className="fas fa-satellite-dish"></i> No agent - data only from the network scan. Serial number, warranty and
        last user come from an agent and are not known for this device.
      </p>
      <div className="row">
        <div className="col-md-6">
          <table className="table table-condensed">
            <tbody>
              <Row label="Host name" value={text(id.hostname)} />
              <Row label="IP address" value={text(id.ip)} />
              <Row label="MAC address" value={text(id.mac)} />
              <Row label="Manufacturer" value={text(id.manufacturer)} />
              <Row
                label="Operating system"
                value={id.os === null ? DASH : id.osGuessed ? `${id.os} (guessed)` : id.os}
              />
              <Row label="Looks like" value={text(id.kind)} />
              <Row label="TTL" value={text(id.ttl)} />
              <Row label="Found by relay" value={text(id.relayName)} />
              <Row label="Last seen" value={text(id.lastSeen)} muted />
            </tbody>
          </table>
        </div>
        <div className="col-md-6">
          <ScanPortsTable ports={scan.ports} />
        </div>
      </div>
    </div>
  )
}

/**
 * Fila Relations - `row_relations.php`.
 *
 * ---------------------------------------------------------------------------
 * DOUA TABELE, NU UNUL CU O COLOANA „Direction"
 * ---------------------------------------------------------------------------
 *
 * Motivul sta scris in capul machetei vechi, si nu e unul de asezare:
 * „Core Banking depends on Oracle" si „Oracle is depended on by Core Banking"
 * sunt ACEEASI muchie citita de la cele doua capete ale ei. Puse la rand in
 * acelasi tabel, singurul lucru care le deosebeste ajunge un cuvant intr-o
 * coloana ingusta - iar un cuvant sarit inseamna o dependenta citita invers,
 * adica raspunsul pe dos la singura intrebare pentru care se deschide fila.
 * Cu doua tabele intitulate, directia n-o mai poarta randul, ci titlul de
 * deasupra lui, si nu se poate sari.
 *
 * Capatul trimite deja cele doua siruri despartite - `outgoing` si `incoming`
 * din `GET /cmdb/ci/{id}` (`CiRecord::page()`, `CmdbRelations::outgoing()` /
 * `::incoming()`), fiecare cu `direction` scris pe rand. Ecranul le lipea
 * inapoi cu un `[...outgoing, ...incoming]`; despartirea nu cere nimic nou de
 * la placa.
 *
 * COLOANELE: macheta veche are aceleasi trei coloane in amandoua tabelele -
 * Relation, Item, Where from - cu clasa ca un rand mic sub numele lucrului.
 * Aici clasa isi pastreaza coloana ei, cum o avea si tabelul unic de pana
 * acum: raspunsul o trimite separat (`itemClass`), iar filele vecine o arata
 * tot pe coloana. Coloana a patra, „Stated by", e „Where from" a machetei -
 * originea, in cuvintele din `CmdbRelations::ORIGIN`. Portalul mai scria,
 * pentru originea 4 („observed traffic"), increderea si de cate ori a fost
 * vazuta; in v4 originea aia nu exista - `ORIGIN` are trei intrari - deci
 * n-are ce scrie.
 *
 * GOLUL SE SPUNE, NU SE ASCUNDE: un tabel care dispare cand e gol lasa pe
 * ecran un singur titlu, si randurile de sub el se citesc drept celalalt fel
 * de legatura - fix rasturnarea pe care cele doua tabele o opresc. Deci
 * amandoua titlurile se scriu mereu, iar cel gol poarta „Nothing.", ca in
 * macheta.
 */
function RelationsPanel({ page }: { page: CiRecordPage }) {
  return (
    <div className="row" style={{ padding: '12px 16px' }}>
      <div className="col-md-6">
        <h5>
          <strong>Depends on / points at</strong>
        </h5>
        <EdgeTable edges={page.outgoing} />
      </div>
      <div className="col-md-6">
        <h5>
          <strong>Depended on by</strong>
        </h5>
        <EdgeTable edges={page.incoming} />
      </div>
    </div>
  )
}

function EdgeTable({ edges }: { edges: CiEdge[] }) {
  if (edges.length === 0) {
    return <p className="text-muted">Nothing.</p>
  }
  return (
    <table className="table table-condensed table-striped">
      <thead>
        <tr>
          <th style={{ width: 150 }}>Relation</th>
          <th>Item</th>
          <th style={{ width: 140 }}>Class</th>
          <th style={{ width: 110 }}>Stated by</th>
        </tr>
      </thead>
      <tbody>
        {edges.map((edge) => (
          <tr key={`${edge.direction}${edge.id}`}>
            <td className="text-muted">
              <EdgeRelation edge={edge} />
            </td>
            <td>
              <a href={`/app/cmdb/view/${edge.itemId}`}>{edge.itemName}</a>
            </td>
            <td className="text-muted">{edge.itemClass}</td>
            <td className="text-muted">{edge.origin}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const RC_CSS = `
.rc-wrap { padding: 12px 16px; }
.rc-toggle { margin-bottom: 14px; }
.rc-toggle .btn { color: #b16131; border-color: #b16131; background: transparent; }
.rc-toggle .btn:hover { background: rgba(177, 97, 49, .1); }
.rc-toggle .btn.active, .rc-toggle .btn.active:hover, .rc-toggle .btn.active:focus { background: #b16131; color: #fff; }
.rc-toggle .btn.active .rc-muted { color: rgba(255, 255, 255, .75); }
.rc-kind { display: inline-block; min-width: 66px; text-align: center; font-size: 11px; font-weight: 600; color: #fff; padding: 1px 8px; border-radius: 10px; }
.rc-change { background: #37addb; }
.rc-syslog { background: #E67E22; }
.rc-link { background: #534F8A; }
.rc-muted { color: #9aa5b1; }
.rc-desc { margin-top: 4px; padding: 6px 8px; border-left: 2px solid #e6eaee; color: #6b747d; font-size: 12px; white-space: pre-wrap; }
.rc-comment { margin-top: 4px; font-size: 12px; color: #6b747d; }
.rc-comment b { color: #3d444b; }
.rc-text { white-space: pre-wrap; }
.rc-scroll { max-height: 420px; overflow-y: auto; }
`

const RC_KINDS: Record<CiChangesPanel['logs'][number]['kind'], [string, string]> = {
  change: ['rc-change', 'Change'],
  syslog: ['rc-syslog', 'Syslog'],
  link: ['rc-link', 'Link'],
}

/**
 * Fila Changes - `row_changes.php`: un singur fir "ce s-a intamplat cu lucrul
 * asta" (drift cu Authorised, legaturi, syslog) si, alaturi, tichetele care
 * l-au numit. Ultimele 90 de zile.
 */
function ChangesPanel({ id }: { id: number }) {
  const panel = useQuery(cmdbChangesQuery(id))
  const [view, setView] = useState<'logs' | 'tickets'>('logs')
  if (panel.error) return <PanelStopped error={panel.error} retry={() => void panel.refetch()} what="The changes" />
  if (!panel.data) return <Spinner />
  const { logs, tickets } = panel.data

  return (
    <div className="rc-wrap" id={`rcTab${id}`}>
      <style>{RC_CSS}</style>
      <div className="btn-group rc-toggle" data-toggle="buttons">
        <label className={`btn btn-theme-inverse btn-transparent btn-sm${view === 'logs' ? ' active' : ''}`} onClick={() => setView('logs')}>
          <input type="radio" name="rcView" autoComplete="off" value="logs" checked={view === 'logs'} readOnly />
          <i className="fas fa-stream"></i> Logs <span className="rc-muted">({logs.length})</span>
        </label>
        <label className={`btn btn-theme-inverse btn-transparent btn-sm${view === 'tickets' ? ' active' : ''}`} onClick={() => setView('tickets')}>
          <input type="radio" name="rcView" autoComplete="off" value="tickets" checked={view === 'tickets'} readOnly />
          <i className="fas fa-ticket-alt"></i> Tickets <span className="rc-muted">({tickets.length})</span>
        </label>
      </div>

      {view === 'logs' && (
        <div className="rc-pane" data-pane="logs">
          {logs.length === 0 ? (
            <p className="rc-muted">
              Nothing has been recorded on this item in the last 90 days.
              <span style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                Configuration changes come from the agent history; syslog has to be pointed at the platform, and the item
                needs its Management IP set.
              </span>
            </p>
          ) : (
            <>
              <div className="rc-scroll">
                <table className="table table-condensed table-striped">
                  <thead>
                    <tr>
                      <th style={{ width: 150 }}>When</th>
                      <th style={{ width: 80 }}>Kind</th>
                      <th style={{ width: 190 }}>What</th>
                      <th>Detail</th>
                      <th style={{ width: 200 }}>Authorised</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((entry, i) => (
                      <tr key={`${entry.kind}-${entry.at}-${i}`}>
                        <td className="rc-muted text-nowrap">{entry.at}</td>
                        <td>
                          <span className={`rc-kind ${RC_KINDS[entry.kind][0]}`}>{RC_KINDS[entry.kind][1]}</span>
                        </td>
                        <td className="rc-muted">
                          {entry.what}
                          {entry.note !== '' && (
                            <>
                              <br />
                              <span style={{ fontSize: 11 }}>{entry.note}</span>
                            </>
                          )}
                        </td>
                        <td>{entry.text}</td>
                        <td>
                          {entry.authorised === null ? (
                            <span className="rc-muted">&mdash;</span>
                          ) : entry.authorised ? (
                            <>
                              <span className="label label-success">yes</span>{' '}
                              {entry.authorisedBy && (
                                <span className="rc-muted small">
                                  {entry.authorisedBy.ticket > 0 ? (
                                    <a href={`/app/easeedesk/ticket/${entry.authorisedBy.ticket}`} target="_blank" rel="noopener">
                                      {entry.authorisedBy.label}
                                    </a>
                                  ) : (
                                    entry.authorisedBy.label
                                  )}
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="label label-warning">no</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="rc-muted small">
                <a href="/app/adm/changes" target="_blank" rel="noopener">
                  All changes, across every item
                </a>
              </p>
            </>
          )}
          {/* Istoricul nu se poate rescrie, si se poate dovedi. */}
          <HistoryIntegrity id={id} />
        </div>
      )}

      {view === 'tickets' && (
        <div className="rc-pane" data-pane="tickets">
          {tickets.length === 0 ? (
            <p className="rc-muted">No ticket has ever named this item.</p>
          ) : (
            <div className="rc-scroll">
              <table className="table table-condensed table-striped">
                <thead>
                  <tr>
                    <th style={{ width: 90 }}>Ticket</th>
                    <th>Subject</th>
                    <th style={{ width: 130 }}>Status</th>
                    <th style={{ width: 140 }}>Created</th>
                    <th style={{ width: 140 }}>Resolved</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((ticket) => (
                    <tr key={ticket.id}>
                      <td>
                        <a href={`/app/easeedesk/ticket/${ticket.id}`} target="_blank" rel="noopener">
                          #{ticket.id}
                        </a>
                      </td>
                      <td>
                        <strong>{ticket.subject}</strong>
                        {ticket.description !== '' && <div className="rc-desc">{ticket.description}</div>}
                        {ticket.comments.map((c, i) => (
                          <div className="rc-comment" key={i}>
                            <i className="far fa-comment rc-muted"></i> <b>{c.author}</b> <span className="rc-muted">{c.created}</span> &mdash;{' '}
                            <span className="rc-text">{c.text}</span>
                          </div>
                        ))}
                      </td>
                      <td>
                        {ticket.status.name !== '' ? (
                          <span className="label" style={{ background: ticket.status.color }}>
                            {ticket.status.name}
                          </span>
                        ) : (
                          <span className="rc-muted">{ticket.status.code}</span>
                        )}
                      </td>
                      <td className="rc-muted">{ticket.created}</td>
                      <td className="rc-muted">{ticket.resolved ?? <span className="rc-muted">open</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** number_format(x, 2, ',', '.') - ca in row_impact.php. */
function money(amount: number): string {
  const [int, dec] = Math.abs(amount).toFixed(2).split('.')
  return `${amount < 0 ? '-' : ''}${int!.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${dec}`
}

/** Cate randuri incap in rezumat - `array_slice($ranked, 0, 15)` din row_impact.php. */
const IMPACT_SHOWN = 15

/**
 * Fila Impact - `row_impact.php`: ce cade daca elementul asta cade.
 *
 * Versiunea scurta a raportului din /adm/impact: numaratorile si lista, fara
 * exporturi. Cine trece printr-o lista vrea sa stie daca lucrul conteaza
 * INAINTE sa se uite ca lumea la el.
 *
 * Cele patru propozitii de deasupra tabelului sunt patru intrebari diferite,
 * si se pun in ordinea in care le pune si un om: cate cad, cate dintre ele
 * conteaza, cat costa, si daca vreunul e in productie. Pana acum se scria
 * numai prima - celelalte trei n-aveau din ce, fiindca `kind` si
 * `environment` se pierdeau in raspuns (vezi `CmdbImpact::rows`). "47 de
 * elemente afectate" nu spune daca cele 47 sunt imprimante dintr-un birou sau
 * serviciul de plati.
 */
function ImpactPanel({ page }: { page: CiRecordPage }) {
  const priced = page.impactPriced
  const rows = page.impact

  if (rows.length === 0) {
    return (
      <div style={{ padding: '12px 16px' }}>
        <p className="text-muted">Nothing depends on this item, so nothing else breaks if it fails.</p>
        <p className="text-muted small">
          That can also mean its dependencies have not been discovered yet.{' '}
          <a href={`/app/adm/map?root=${page.ci.id}`} target="_blank" rel="noopener">
            The dependency map
          </a>{' '}
          shows what is known.
        </p>
      </div>
    )
  }

  /* Aceleasi numaratori ca in row_impact.php, pe aceleasi praguri. */
  const critical = rows.filter((row) => row.importance.level >= 4).length
  const services = rows.filter((row) => row.kind >= 3).length
  /*
   * Productia se numara separat, si tot separat se numara cate au mediul
   * completat. „3 din 47 sunt productie" inseamna cu totul altceva daca pentru
   * 40 dintre ele nu stie nimeni - iar diferenta e fix ce hotaraste daca cifra
   * se poate lua in serios.
   */
  const knownEnv = rows.filter((row) => row.environment > 0).length
  const production = rows.filter((row) => row.environment === 1).length

  return (
    <div style={{ padding: '12px 16px' }}>
      <p>
        <strong>{rows.length}</strong> item(s) depend on this, of which <strong>{critical}</strong> important or
        critical
        {services > 0 && (
          <>
            {' '}
            and <strong>{services}</strong> service(s)
          </>
        )}
        .
      </p>

      {priced && priced.count > 0 && (
        <p>
          Among them, <strong>{priced.count}</strong> catalog service(s), worth{' '}
          <strong>
            {Object.entries(priced.totals)
              .map(([currency, amount]) => `${money(amount)} ${currency}`)
              .join(' + ')}
          </strong>{' '}
          a month in total:{' '}
          {priced.top.map((service, i) => (
            <span key={service.id}>
              {i > 0 ? ', ' : ''}
              <a href={`/app/cmdb/view/${service.id}`} target="_blank" rel="noopener">
                {service.name}
              </a>{' '}
              <span className="text-muted">
                ({money(service.amount)} {service.currency})
              </span>
            </span>
          ))}
          {priced.count > 2 && <span className="text-muted"> and {priced.count - 2} more</span>}
        </p>
      )}

      {knownEnv > 0 && (
        <p>
          <strong>{production}</strong> of them in production.
          {knownEnv < rows.length && (
            <span className="text-muted small">
              {' '}
              Counted over the {knownEnv} item(s) whose environment is recorded; {rows.length - knownEnv} have none
              set.
            </span>
          )}
        </p>
      )}

      <table className="table table-condensed table-striped">
        <thead>
          <tr>
            <th>Item</th>
            <th style={{ width: 170 }}>Class</th>
            <th style={{ width: 110 }}>Importance</th>
            <th style={{ width: 130 }} className="text-right">
              Cost / month
            </th>
            <th style={{ width: 90 }} className="text-center">
              Steps away
            </th>
          </tr>
        </thead>
        <tbody>
          {/* Taiata, nu paginata: asta e un rezumat, iar raportul intreg e la o
              apasare. Ca a fost taiata conteaza mai mult decat restul randurilor
              - serverul le-a asezat deja dupa consecinta (`CmdbImpact::rows`). */}
          {rows.slice(0, IMPACT_SHOWN).map((row) => (
            <tr key={row.id}>
              <td>
                <a href={`/app/cmdb/view/${row.id}`} target="_blank" rel="noopener">
                  {row.name}
                </a>
              </td>
              <td className="text-muted">{row.className}</td>
              <td>
                <span className={row.importance.class}>{row.importance.label}</span>
              </td>
              <td className="text-right">
                {row.cost === null || row.cost === undefined ? (
                  /* Liniuta, nu zero: un switch nu costa zero lei pe luna, doar
                     nu e un serviciu facturat. Zero ar fi un raspuns; asta e
                     lipsa unei intrebari. */
                  <span className="adm-muted">&mdash;</span>
                ) : (
                  <>
                    <strong>{money(row.cost.amount)}</strong>{' '}
                    <span className="text-muted small">{row.cost.currency}</span>
                  </>
                )}
              </td>
              <td className="text-center text-muted">{row.depth}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="text-muted small">
        {rows.length > IMPACT_SHOWN && (
          <>
            Showing the {IMPACT_SHOWN} most important of {rows.length}.{' '}
          </>
        )}
        <a href={`/app/adm/impact/${page.ci.id}`} target="_blank" rel="noopener">
          Full report, with exports
        </a>
      </p>
    </div>
  )
}

/* -------------------------------------------------------------------------- *
 * Filele cu capat propriu
 * -------------------------------------------------------------------------- */

function EventsTab({ id }: { id: number }) {
  const [filters, setFilters] = useState<{ days: number; severity: string; facility: string; object: string; q: string }>({
    days: 30,
    severity: '',
    facility: '',
    object: '',
    q: '',
  })
  const panel = useQuery(cmdbSyslogQuery(id, filters))

  if (panel.error) return <PanelStopped error={panel.error} retry={() => void panel.refetch()} what="The events" />
  if (!panel.data) return <Spinner />

  const data: CiSyslogPanel = panel.data

  if (!data.hasTable) {
    return (
      <p className="text-muted" style={{ padding: '12px 16px' }}>
        The syslog table is not in the database yet.
      </p>
    )
  }

  return (
    <div style={{ padding: '12px 16px' }} id={`slTab${id}`}>
      <div style={{ marginBottom: 14 }}>
        <select
          className="form-control input-sm"
          style={{ display: 'inline-block', width: 'auto', marginRight: 8 }}
          aria-label="Period"
          value={filters.days}
          onChange={(e) => setFilters({ ...filters, days: Number(e.target.value) })}
        >
          <option value={1}>Last 24 hours</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        <select
          className="form-control input-sm"
          style={{ display: 'inline-block', width: 'auto', marginRight: 8 }}
          aria-label="Severity"
          value={filters.severity}
          onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
        >
          <option value="">Any severity</option>
          {data.severities.map((pair) => (
            <option key={pair.value} value={pair.value}>
              {pair.label} and worse
            </option>
          ))}
        </select>
        <select
          className="form-control input-sm"
          style={{ display: 'inline-block', width: 'auto', marginRight: 8 }}
          aria-label="Facility"
          value={filters.facility}
          onChange={(e) => setFilters({ ...filters, facility: e.target.value })}
        >
          <option value="">Any facility</option>
          {data.facilities.map((row) => (
            <option key={row.facility} value={row.facility}>
              {row.facility} ({row.total})
            </option>
          ))}
        </select>
        <input
          type="search"
          className="form-control input-sm"
          style={{ display: 'inline-block', width: 220 }}
          placeholder="Search the message"
          aria-label="Search the message"
          value={filters.q}
          onChange={(e) => setFilters({ ...filters, q: e.target.value })}
        />
      </div>

      {data.objects.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {/* Ce a fluturat cel mai des - singura intrebare pusa de cineva care se
              uita la syslogul unui switch. Al doilea clic scoate filtrul. */}
          {data.objects.map((row) => (
            <span
              key={row.object}
              className={filters.object === row.object ? 'sl-chip active' : 'sl-chip'}
              role="button"
              tabIndex={0}
              onClick={() => setFilters({ ...filters, object: filters.object === row.object ? '' : row.object })}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setFilters({ ...filters, object: filters.object === row.object ? '' : row.object })
                }
              }}
            >
              {row.object} <strong>{row.total}</strong>
            </span>
          ))}
        </div>
      )}

      {data.events.length === 0 ? (
        <p className="text-muted">Nothing was received from this item in that period.</p>
      ) : (
        <>
          <table className="table table-condensed table-striped">
            <thead>
              <tr>
                <th style={{ width: 170 }}>When</th>
                <th style={{ width: 100 }}>Severity</th>
                <th style={{ width: 170 }}>Facility</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {data.events.map((event) => (
                <tr key={event.id}>
                  <td className="text-muted">
                    {event.at}
                    {event.received && <span style={{ fontSize: 10 }}> received</span>}
                  </td>
                  <td>
                    <span
                      className="sl-sev"
                      style={{ background: event.severityColour, color: '#fff', padding: '2px 6px', borderRadius: 3 }}
                    >
                      {event.severityLabel}
                    </span>
                  </td>
                  <td>
                    {event.parsed ? (
                      <>
                        {event.facility} <span className="text-muted">{event.mnemonic}</span>
                      </>
                    ) : (
                      <span className="text-muted">unparsed</span>
                    )}
                  </td>
                  <td>{event.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-muted" style={{ fontSize: 12 }}>
            Newest {data.events.length} lines.
          </p>
        </>
      )}
    </div>
  )
}

function ProblemsTab({ id }: { id: number }) {
  const panel = useQuery(cmdbProblemsQuery(id))

  if (panel.error) return <PanelStopped error={panel.error} retry={() => void panel.refetch()} what="The problems" />
  if (!panel.data) return <Spinner />

  const data = panel.data

  if (!data.hasTables) {
    return (
      <p className="text-muted" style={{ padding: '12px 16px' }}>
        The problem tables are not in the database yet.
      </p>
    )
  }

  return (
    <div style={{ padding: '12px 16px' }}>
      {data.pattern !== null && (
        /* Tiparul INAINTEA problemelor scrise: cazul care conteaza e elementul
           cu incidente in serie si nicio problema deschisa - adica exact starea
           in care nimeni n-a observat inca. */
        <div className="adm-panel" style={{ borderLeft: '3px solid #c2571f', marginBottom: 14 }} id="cmdbPattern">
          <p style={{ margin: 0 }}>
            <i className="fas fa-exclamation-triangle"></i> <strong>{data.pattern.incidents}</strong>{' '}
            incidents on this item in the last 90 days, with no problem opened.
          </p>
          <p className="text-muted small" style={{ margin: '6px 0 0' }}>
            First {data.pattern.firstSeen}, last {data.pattern.lastSeen}.{' '}
            <a href="/app/problems/candidates" target="_blank" rel="noopener">
              See the patterns
            </a>{' '}
            to open one.
          </p>
        </div>
      )}

      {data.problems.length === 0 ? (
        <>
          <p className="text-muted">No problem recorded on this item.</p>
          {data.pattern === null && (
            <p className="text-muted small">
              Nor enough incidents to make a pattern. That is not a guarantee — a ticket that does not
              say which item it was opened on is counted nowhere.
            </p>
          )}
        </>
      ) : (
        <table className="table table-condensed table-striped">
          <thead>
            <tr>
              <th style={{ width: 60 }}>#</th>
              <th>Problem</th>
              <th style={{ width: 150 }}>Status</th>
              <th style={{ width: 90 }} className="center">
                Incidents
              </th>
            </tr>
          </thead>
          <tbody>
            {data.problems.map((problem) => (
              <tr key={problem.id}>
                <td>
                  <a href={`/app/problems/view/${problem.id}`} target="_blank" rel="noopener">
                    {problem.id}
                  </a>
                </td>
                <td>
                  <a href={`/app/problems/view/${problem.id}`} target="_blank" rel="noopener">
                    {problem.title}
                  </a>
                  {problem.workaround !== '' && (
                    <div className="text-muted small" style={{ marginTop: 3 }}>
                      <i className="fas fa-life-ring"></i> {problem.workaround}
                    </div>
                  )}
                </td>
                <td>
                  <span className="adm-pill" style={{ background: problem.statusColour, color: '#fff' }}>
                    {problem.statusLabel}
                  </span>
                </td>
                <td className="center">{problem.incidents}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

/**
 * Fila Security - `row_security.php`: ce vulnerabilitati ajung la element, si
 * pe ce drum.
 *
 * Perechea filei de impact. Impactul spune ce cade daca elementul cade; asta
 * spune pe unde poate fi impins sa cada. Pentru un serviciu de business, "ajung
 * la el" inseamna prin statiile de sub el, oricat de departe - de-aia fiecare
 * CVE isi arata drumul.
 */
function SecurityTab({ id }: { id: number }) {
  const panel = useQuery(cmdbSecurityQuery(id))

  if (panel.error) return <PanelStopped error={panel.error} retry={() => void panel.refetch()} what="The exposure" />
  if (!panel.data) return <Spinner />

  const data = panel.data

  return (
    <div style={{ padding: '12px 16px' }} id="cmdbSecurityTab">
      {data.agentless ? (
        <>
          {/* Un aparat descoperit n-are agent, deci nici lista de programe din
              care se potrivesc vulnerabilitatile. „No known endpoint" s-ar citi
              ca „nimic de vazut"; ce se stie e ce a gasit scanarea deschis. */}
          <p className="text-muted">
            No agent runs on this device, so its installed software is not known and no vulnerability can be matched.
            What the network scan found open:
          </p>
          <ScanPortsTable ports={data.scanPorts ?? []} />
        </>
      ) : !data.hasCveTable ? (
        <p className="text-muted">The vulnerability database is not on this instance yet.</p>
      ) : data.devicesTotal === 0 ? (
        <>
          {/*
            Zero statii sub element NU inseamna "nu e expus". Inseamna ca nu
            stim - ori elementul chiar n-are nimic dedesubt, ori dependentele
            lui n-au fost descoperite. Deosebirea conteaza enorm pentru cine
            citeste, deci se scrie, nu se lasa un ecran gol.
          */}
          <p className="text-muted">Nothing can be said here: no known endpoint sits under this item.</p>
          <p className="text-muted small">
            A service with no recorded dependencies looks exactly like one that genuinely has none.{' '}
            <a href={`/app/adm/map?root=${id}`} target="_blank" rel="noopener">
              The dependency map
            </a>{' '}
            tells you which of the two it is.
          </p>
        </>
      ) : data.totalCves === 0 ? (
        <>
          <p>
            No known vulnerability on the <strong>{data.devicesChecked}</strong> endpoints under this item.
          </p>
          <p className="text-muted small">
            Installed software is matched against the CVE catalog. Software the catalog does not know about will not show here —
            absence is not a guarantee. How fresh the database is says below.
          </p>
        </>
      ) : (
        <>
          <p style={{ marginBottom: 10 }}>
            <strong>{data.totalCves}</strong> distinct vulnerabilities reach this item, through{' '}
            <strong>{data.devicesChecked}</strong> endpoints.{' '}
            {/*
              Cate ating productia, ca propozitie separata. "31 de
              vulnerabilitati" e o cifra de raport; "6 dintre ele ating
              productia" e cifra dupa care se hotaraste ce se face in ora
              urmatoare.
            */}
            {data.productionCves > 0 && (
              <strong style={{ color: SEVERITY_PRODUCTION }}>{data.productionCves} touch production.</strong>
            )}
          </p>

          <p style={{ marginBottom: 14 }}>
            {SEVERITY_ORDER.filter((level) => (data.counts[level] ?? 0) > 0).map((level) => (
              <span key={level} style={{ display: 'inline-block', marginRight: 14 }}>
                <span
                  style={{
                    display: 'inline-block',
                    width: 10,
                    height: 10,
                    borderRadius: 2,
                    background: SEVERITY_COLOURS[level],
                    marginRight: 5,
                  }}
                ></span>
                <strong>{data.counts[level]}</strong> {SEVERITY_LABELS[level]}
              </span>
            ))}
          </p>

          {data.partial && (
            /*
             * Se spune apasat, nu ca nota de subsol. O numaratoare de securitate
             * care se opreste tacut la un buget se citeste ca "atatea sunt", si
             * aia e chiar felul de greseala pe care fila exista s-o repare.
             */
            <p className="text-warning" style={{ marginBottom: 12 }} id="cmdbSecurityPartial">
              <i className="fas fa-exclamation-triangle"></i> The count is <strong>partial</strong>:{' '}
              {data.devicesChecked} endpoints of {data.devicesTotal} were looked at, nearest first.
            </p>
          )}

          <table className="table table-condensed table-striped">
            <thead>
              <tr>
                <th style={{ width: 130 }}>CVE</th>
                <th style={{ width: 90 }}>Severity</th>
                <th>Software</th>
                <th>Through</th>
              </tr>
            </thead>
            <tbody>
              {data.cves.map((cve) => (
                <tr key={cve.id}>
                  <td>
                    <a href={`https://nvd.nist.gov/vuln/detail/${cve.id}`} target="_blank" rel="noopener">
                      {cve.id}
                    </a>
                  </td>
                  <td>
                    <span style={{ color: cve.severityColour, fontWeight: 600 }}>{cve.severityLabel}</span>{' '}
                    {cve.score !== null && cve.score !== '' && <span className="text-muted small">{cve.score}</span>}
                  </td>
                  <td>{cve.products.join(', ')}</td>
                  <td>
                    {cve.devices.map((device, i) => (
                      <span key={device.ciId}>
                        {i > 0 ? ', ' : ''}
                        <a href={`/app/cmdb/view/${device.ciId}`} target="_blank" rel="noopener">
                          {device.name}
                        </a>
                        {/* Numai productia primeste eticheta: una pe fiecare
                            statie ar fi facut coloana ilizibila, iar deosebirea
                            care schimba o hotarare e doar asta. */}
                        {device.environment === 1 && <span className="adm-pill adm-pill-warn">prod</span>}
                      </span>
                    ))}
                    {cve.more > 0 && <span className="text-muted"> and another {cve.more}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {data.truncated && (
            <p className="text-muted small">
              Showing the most severe {data.cves.length} of {data.totalCves}.
            </p>
          )}
        </>
      )}

      {/*
        Cat de proaspata e baza din care s-a raspuns - la CAPAT, dupa raspuns.
        Sus ar fi fost o intampinare tehnica pentru cineva venit sa afle
        altceva; la capat e nota care spune cat sa te bizui pe ce tocmai ai
        citit. Se scrie MEREU, si cand e bine.
      */}
      <p className={`cve-freshness cve-freshness-${data.freshness.tone}`}>
        <i className={`fas ${data.freshness.tone === 'ok' ? 'fa-database' : 'fa-exclamation-triangle'}`}></i>
        <span>
          {data.freshness.text}
          {data.freshness.hint !== '' && <span className="cve-freshness-hint">{data.freshness.hint}</span>}
        </span>
      </p>
      <style>{SECURITY_CSS}</style>
    </div>
  )
}

/* -------------------------------------------------------------------------- *
 * Cele doua feluri de „nu se poate"
 * -------------------------------------------------------------------------- */

/**
 * Ce se deseneaza cand fila n-are ce desena, in ordinea in care se pun
 * intrebarile: n-ai voie, nu e al tau, a picat.
 *
 * Ordinea nu e de stil. Pana acum orice eroare in afara de 404 ajungea la
 * „That could not be loaded." - o propozitie fara buton, dupa care singura
 * miscare ramasa omului era sa stranga randul si sa-l desfaca la loc. Iar
 * cand cererea nu se termina deloc, `data` ramane `undefined` pentru
 * totdeauna si fila se invarte la nesfarsit: exact felul in care o fila
 * SCRISA se citeste ca o fila NESCRISA, si ajunge raportata ca „nu e
 * implementata".
 */
function PanelStopped({ error, retry, what }: { error: unknown; retry: () => void; what: string }) {
  if (isForbidden(error)) return <NoAccess />
  if (error instanceof ApiError && error.status === 404) return <Refused />

  return (
    <div style={{ padding: '12px 16px' }}>
      <LoadFailed error={error} onRetry={retry} what={what} />
    </div>
  )
}

/**
 * Fara drept, si se spune - nu se deseneaza o fila goala.
 *
 * `Forbidden` din `ui/` e ecranul INTREG, cu titlu si buton de intoarcere
 * acasa; inauntrul unui rand desfacut ar fi fost o pagina indesata intr-o
 * celula. Acelasi lucru spus, pe masura locului.
 */
function NoAccess() {
  return (
    <p className="text-muted" style={{ padding: '12px 16px' }}>
      <i className="fas fa-lock" aria-hidden="true"></i> You do not have the rights to see this.
    </p>
  )
}

/** Refuzat, nu gol: elementul nu e al firmelor omului, si asta se SPUNE. */
function Refused() {
  return (
    <p className="text-muted" style={{ padding: '12px 16px' }}>
      This item belongs to another customer.
    </p>
  )
}

function Row({ label, value, muted }: { label: string; value: React.ReactNode; muted?: boolean }) {
  return (
    <tr>
      <td style={{ width: 150 }} className="text-muted">
        {label}
      </td>
      <td className={muted === true ? 'text-muted' : undefined}>{value}</td>
    </tr>
  )
}
